import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useMnaAssets } from '../lib/api';
import type { AssetDto, AssetKind } from '../types';
import { ConfidenceValue, SourceLink, scoreTone } from './_shared';

// Tab 5 — Assets. First-class asset tracking (ADR-0009 §1): product lines, aftermarket
// books, facilities and §363 auction lots, each with its own fit score and provenance.
// Filterable by kind and company; rows link back to the owning company's dossier.

const KIND_LABELS: Record<AssetKind, string> = {
  product_line: 'Product line',
  aftermarket_book: 'Aftermarket book',
  facility: 'Facility',
  auction_lot: 'Auction lot',
};

type AssetStatus = AssetDto['status'];

const STATUS_LABELS: Record<AssetStatus, string> = {
  tracked: 'Tracked',
  for_sale: 'For sale',
  sold: 'Sold',
  withdrawn: 'Withdrawn',
};

const STATUS_STYLES: Record<AssetStatus, string> = {
  tracked: 'border-border text-muted',
  for_sale: 'border-accent-warn text-accent-warn',
  sold: 'border-score-high text-score-high',
  withdrawn: 'border-border text-muted opacity-60',
};

const selectClass = cn(
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11',
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent'
);

export default function Assets() {
  const { data, isLoading, error } = useMnaAssets();
  const assets = useMemo(() => data ?? [], [data]);

  const [kind, setKind] = useState('');
  const [company, setCompany] = useState('');

  const companyOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of assets) m.set(a.companySlug, a.companyName);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [assets]);

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error) return <ErrorState message={(error as Error).message} />;

  const filtered = assets.filter(
    (a) => (kind === '' || a.kind === kind) && (company === '' || a.companySlug === company)
  );

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Assets</CardTitle>
        <CardDescription className="mt-2">
          Tracked assets — product lines, aftermarket books, facilities and auction lots. An
          acquisition target is sometimes an asset, not a company (ADR-0009).
        </CardDescription>
      </Card>

      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">Kind</span>
            <select className={selectClass} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">all</option>
              {(Object.keys(KIND_LABELS) as AssetKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">Company</span>
            <select className={selectClass} value={company} onChange={(e) => setCompany(e.target.value)}>
              <option value="">all</option>
              {companyOptions.map(([slug, name]) => (
                <option key={slug} value={slug}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <span className="ml-auto pb-2 text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
            {filtered.length} of {assets.length} assets
          </span>
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <CardDescription>
            {assets.length === 0 ? 'No assets tracked yet — they spawn from auction and divestiture events.' : 'No assets match the current filters.'}
          </CardDescription>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((a) => (
            <AssetRow key={a.id} asset={a} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One asset row — also embedded in the company dossier (linkCompany=false there). */
export function AssetRow({ asset: a, linkCompany = true }: { asset: AssetDto; linkCompany?: boolean }) {
  return (
    <div className="rounded-(--radius-md) border border-border bg-surface p-4 shadow-[var(--shadow-sm)]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-block rounded-(--radius-sm) border border-accent bg-surface-elevated px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em] text-accent">
          {KIND_LABELS[a.kind]}
        </span>
        <span
          className={cn(
            'inline-block rounded-(--radius-sm) border bg-surface-elevated px-2 py-0.5 font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em]',
            STATUS_STYLES[a.status]
          )}
        >
          {STATUS_LABELS[a.status]}
        </span>
        <span className="ml-auto flex items-center gap-3">
          {a.fitScore !== null && (
            <span className="font-(family-name:--font-ui) text-sm [font-variant-numeric:tabular-nums]">
              <span className="text-[0.65rem] uppercase tracking-[0.12em] text-muted">Fit </span>
              <span className={cn('font-semibold', scoreTone(a.fitScore))}>{a.fitScore}</span>
            </span>
          )}
          {a.source && <SourceLink source={a.source} />}
        </span>
      </div>

      <h3 className="mt-2 text-base font-semibold text-text">
        <ConfidenceValue value={a.name} confidence={a.confidence} source={null} />
      </h3>
      {a.description && <p className="mt-1 text-sm text-muted">{a.description}</p>}

      {linkCompany && (
        <Link
          to={`/mna/companies/${a.companySlug}`}
          className="mt-2 inline-block text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {a.companyName}
        </Link>
      )}
    </div>
  );
}
